import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { formatCurrency } from '../utils/currency';
import useCurrencyCode from '../hooks/useCurrencyCode';

const formatUkDateTime = (value) => {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const resolveQueryRef = (search = '') => {
  const ref = new URLSearchParams(search).get('ref');
  return typeof ref === 'string' ? ref.trim() : '';
};

export default function CustomerPreorderTrackingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const currencyCode = useCurrencyCode();
  const initialRef = useMemo(() => resolveQueryRef(location.search), [location.search]);
  const [reference, setReference] = useState(initialRef);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTracking = async (refValue) => {
    const trimmed = (refValue || '').trim();
    if (!trimmed) {
      toast.error('Enter your order reference.');
      return;
    }
    setResult(null);
    setLoading(true);
    try {
      const res = await api.get('/preorders/track', { params: { ref: trimmed } });
      setResult(res.data || null);
    } catch (err) {
      const status = err?.response?.status;
      const payload = err?.response?.data;
      if (status === 404) {
        toast.error('Reference not found — check and try again.');
      } else if (status === 429) {
        const retryAfter = payload?.retryAfterSeconds;
        const message = payload?.message || 'Too many requests. Please try again shortly.';
        toast.error(retryAfter ? `${message} Try again in ${retryAfter}s.` : message);
      } else {
        toast.error(payload?.message || 'Something went wrong — please try again.');
      }
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialRef) {
      fetchTracking(initialRef);
    }
  }, [initialRef]);

  const handleSubmit = (event) => {
    event.preventDefault();
    fetchTracking(reference);
  };

  const total = Number(result?.total || 0);
  const statusLabel = result?.statusLabel || result?.status || 'Updated';
  const resolvedCurrency = result?.currency || currencyCode;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 pb-24 pt-10 text-white">
      <main className="mx-auto flex max-w-3xl flex-col gap-6">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-[0.5em] text-slate-500">Ofoodi Local</p>
          <h1 className="text-3xl font-semibold">Track your pre-order</h1>
          <p className="text-sm text-slate-400">
            Enter the order reference from your confirmation page to see the latest status.
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          className="rounded-3xl border border-slate-800/70 bg-slate-900/70 p-5 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)] backdrop-blur-xl"
        >
          <label className="text-xs uppercase tracking-[0.3em] text-slate-500" htmlFor="order-ref">
            Order reference
          </label>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row">
            <input
              id="order-ref"
              type="text"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder="Enter your reference"
              className="flex-1 rounded-2xl border border-slate-800 bg-slate-950 px-4 py-3 text-sm text-white outline-none transition focus:border-emerald-400"
            />
            <button
              type="submit"
              disabled={loading}
              className="rounded-2xl bg-emerald-400 px-5 py-3 text-sm font-semibold text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading ? 'Checking…' : 'Check status'}
            </button>
          </div>
        </form>

        {result ? (
          <section className="rounded-3xl border border-slate-800/70 bg-slate-900/70 p-5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Status</p>
                <p className="text-2xl font-semibold text-white">{statusLabel}</p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/preorders/new')}
                className="rounded-2xl border border-slate-700 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-slate-200"
              >
                Place another order
              </button>
            </div>

            <div className="mt-4 grid gap-4 border-t border-slate-800/70 pt-4 sm:grid-cols-3">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Reference</p>
                <p className="text-sm text-white">{result.publicRef}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Last update</p>
                <p className="text-sm text-white">{formatUkDateTime(result.updatedAt)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Total</p>
                <p className="text-sm text-white">
                  {formatCurrency(total, resolvedCurrency)}
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-800/70 bg-slate-950/40 p-4 text-sm text-slate-300">
              <p>Items: {result.itemCount || 0}</p>
              {result.pickupTime && (
                <p className="mt-1 text-xs text-slate-400">
                  Pickup time: {result.pickupTime}
                </p>
              )}
            </div>

            {Array.isArray(result.timeline) && result.timeline.length > 0 && (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Timeline</p>
                <div className="mt-2 space-y-2 text-sm text-slate-200">
                  {result.timeline.map((entry, index) => (
                    <div key={`${entry.status}-${index}`} className="flex justify-between">
                      <span>{entry.status}</span>
                      <span className="text-slate-400">{formatUkDateTime(entry.at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        ) : (
          <section className="rounded-3xl border border-slate-800/70 bg-slate-900/40 p-5 text-sm text-slate-400">
            Enter your order reference to see the latest status. If you just placed an order,
            use the reference shown on your confirmation page.
          </section>
        )}
      </main>
    </div>
  );
}
